import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { X, Printer } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";

const EMPTY = { name: "", phone: "", email: "", date_start: "", date_end: "", message: "" };

export const ReservationModal = ({ open, listing, onClose }) => {
  const [form, setForm] = useState(EMPTY);
  const [sending, setSending] = useState(false);
  const [reference, setReference] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    if (open) {
      setReference(null);
      setForm((f) => ({ ...EMPTY, name: f.name, phone: f.phone, email: f.email }));
    }
  }, [open]);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      const { data } = await api.post("/reservations", {
        ...form,
        listing_id: listing?.id || null,
        listing_title: listing?.title || "Réservation",
      });
      setReference(data.reference);
      toast.success("Réservation enregistrée !");
    } catch {
      toast.error("Envoi impossible. Réessayez ou appelez-nous.");
    } finally {
      setSending(false);
    }
  };

  const inputCls =
    "w-full bg-[#0D1117] border border-[#21262D] rounded-xl px-4 py-3 text-sm text-[#F0F6FC] placeholder:text-[#8B949E]/60 focus:outline-none focus:border-[#F2541B] transition-colors duration-300 [color-scheme:dark]";

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-[100] bg-[#0D1117]/85 backdrop-blur-sm flex items-end sm:items-center justify-center p-0 sm:p-6"
          onClick={onClose}
          data-testid="reservation-modal-overlay"
        >
          <motion.div
            initial={{ y: 60, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            exit={{ y: 60, opacity: 0 }}
            transition={{ duration: 0.4, ease: [0.16, 1, 0.3, 1] }}
            onClick={(e) => e.stopPropagation()}
            data-testid="reservation-modal"
            className="w-full sm:max-w-lg bg-[#161B22] border border-[#21262D] rounded-t-3xl sm:rounded-3xl p-7 sm:p-9 max-h-[90vh] overflow-y-auto"
          >
            <div className="flex items-start justify-between mb-2">
              <div>
                <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#E5A93C] mb-2">
                  Paiement sur place au bureau
                </p>
                <h3 className="font-display font-bold uppercase tracking-tight text-2xl text-[#F0F6FC]">
                  Réserver en ligne
                </h3>
              </div>
              <button onClick={onClose} data-testid="reservation-close-button" className="text-[#8B949E] hover:text-[#F0F6FC] transition-colors" aria-label="Fermer">
                <X size={22} />
              </button>
            </div>

            {reference ? (
              <div className="mt-6 text-center" data-testid="reservation-success">
                <p className="text-sm text-[#8B949E] mb-3">Votre réservation est enregistrée. Voici votre référence :</p>
                <p className="font-mono font-bold text-3xl text-[#F2541B] tracking-widest" data-testid="reservation-reference">
                  {reference}
                </p>
                <p className="text-sm text-[#8B949E] leading-relaxed mt-5 mb-7">
                  Imprimez votre bon de réservation et présentez-le au bureau TPLM,
                  16 rue Simon Sacapou à Maripasoula, pour finaliser et payer sur place.
                </p>
                <div className="flex flex-col sm:flex-row gap-3 justify-center">
                  <button
                    onClick={() => navigate(`/reservation/${reference}`)}
                    data-testid="reservation-view-bon-btn"
                    className="flex items-center justify-center gap-2 bg-[#F2541B] hover:bg-[#D9420B] text-white font-display font-bold uppercase tracking-wide px-6 py-3.5 rounded-full transition-colors duration-300"
                  >
                    <Printer size={16} /> Voir mon bon
                  </button>
                  <button
                    onClick={onClose}
                    data-testid="reservation-done-btn"
                    className="border border-[#21262D] hover:border-[#8B949E] text-[#F0F6FC] font-display font-bold uppercase tracking-wide px-6 py-3.5 rounded-full transition-colors duration-300"
                  >
                    Terminer
                  </button>
                </div>
              </div>
            ) : (
              <>
                {listing && (
                  <p className="font-mono text-xs text-[#F2541B] border border-[#F2541B]/30 rounded-lg px-3 py-2 mb-5 mt-2" data-testid="reservation-preset-listing">
                    Réservation : {listing.title}
                  </p>
                )}
                <form onSubmit={submit} className="flex flex-col gap-4 mt-4" data-testid="reservation-form">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block font-mono text-[10px] uppercase tracking-wider text-[#8B949E] mb-1.5">Du *</label>
                      <input required type="date" value={form.date_start} onChange={set("date_start")} className={inputCls} data-testid="reservation-date-start" />
                    </div>
                    <div>
                      <label className="block font-mono text-[10px] uppercase tracking-wider text-[#8B949E] mb-1.5">Au</label>
                      <input type="date" value={form.date_end} onChange={set("date_end")} className={inputCls} data-testid="reservation-date-end" />
                    </div>
                  </div>
                  <input required placeholder="Votre nom *" value={form.name} onChange={set("name")} className={inputCls} data-testid="reservation-name-input" />
                  <input required placeholder="Téléphone *" value={form.phone} onChange={set("phone")} className={inputCls} data-testid="reservation-phone-input" />
                  <input type="email" placeholder="Email (optionnel)" value={form.email} onChange={set("email")} className={inputCls} data-testid="reservation-email-input" />
                  <textarea rows={2} placeholder="Précisions (optionnel)" value={form.message} onChange={set("message")} className={`${inputCls} resize-none`} data-testid="reservation-message-input" />
                  <button
                    type="submit"
                    disabled={sending}
                    data-testid="reservation-submit-button"
                    className="bg-[#F2541B] hover:bg-[#D9420B] disabled:opacity-60 text-white font-display font-bold uppercase tracking-wide px-6 py-4 rounded-full transition-colors duration-300"
                  >
                    {sending ? "Envoi en cours…" : "Confirmer ma réservation"}
                  </button>
                  <p className="font-mono text-[10px] text-[#8B949E] text-center leading-relaxed">
                    Aucun paiement en ligne — vous payez au bureau lors du retrait.
                  </p>
                </form>
              </>
            )}
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};
